import React, { useEffect, useMemo, useReducer, useRef } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import useMultiStepForm from '../../../Hooks/useMultiStepForm';

import { COLORS } from '../../../Constant/COLORS';

import FireBase from '../../../lib/fireBaseProfessional';
import { LOGOUT } from '../../../lib/Token';

import Header from '../../../Components/Header';
import Confirmation from './Components/Confirmation';
import ContactInformation from './Components/ContactInformation';
import EmailVerification from './Components/EmailVerification';
import FormContext from './Components/FormContext';
import JobInformation from './Components/JobInformation';
import MobileNumberVerification from './Components/MobileNumberVerification';
import Notification from './Components/Notification';
import PersonalInformation from './Components/PersonalInformation';

const STEP_LABELS = [
  'Contact',
  'Verify mobile',
  'Verify email',
  'Your details',
  'Your service',
  'Review',
];

function Registration() {
  const { CurrentStep, nextStep, stepTo, currentStep } = useMultiStepForm([
    ContactInformation,
    MobileNumberVerification,
    EmailVerification,
    PersonalInformation,
    JobInformation,
    Confirmation,
    Notification,
  ]);

  const [inputs, updateInputs] = useReducer(
    (state, field) => ({ ...state, ...field }),
    {
      mobileNumber: '',
      email: '',
      firstName: '',
      lastName: '',
      address: '',
      province: '',
      postalCode: '',
      district: '',
      password: '',
      confirmPassword: '',
      about: '',
      fbUid: '',
    }
  );

  const [serviceInputs, updateServiceInputs] = useReducer(
    (state, field) => ({ ...state, ...field }),
    {
      name: '',
      location: '',
      hotlineNumber: '',
      websiteUrl: '',
      facebookUrl: '',
      description: '',
    }
  );

  const otpReferenceId = useRef({
    email: '',
    mobileNumber: '',
  }).current;

  // Service packages collected at the Service Details step; created (attached to
  // the new service) at Confirmation. Kept here so they survive step navigation.
  const servicePackages = useRef([]).current;

  // Service images are staged here until the Service Details step uploads them.
  // The professional avatar + KYC docs are uploaded directly at the Personal
  // Details step, so they are not staged here.
  const imageFiles = useRef({
    bannerImage: null, // service
    serviceImages: [], // service
    icon: null, // service
  }).current;

  // S3 URLs for all registration images. The avatar/NIC/address-proof URLs are
  // populated at the Personal Details step; the service image URLs at the Service
  // Details step. The final submit (Confirmation) only creates the DB rows.
  const uploadedUrls = useRef({
    avatar: null,
    nicImage: null,
    addressProof: null,
    bannerImage: null,
    serviceImages: [],
    icon: null,
  }).current;

  // Tracks whether a Firebase user was created (at the Personal Details step)
  // and whether registration finished, so we can clean up an abandoned signup.
  const registrationState = useRef({ fbUserCreated: false, completed: false }).current;

  // Guard: if the partner created a Firebase account but navigated away before
  // finishing, delete the orphaned Firebase user (best-effort).
  useEffect(() => {
    return () => {
      if (registrationState.fbUserCreated && !registrationState.completed) {
        FireBase.deleteProfessional().catch(() => undefined);
        LOGOUT();
      }
    };
  }, [registrationState]);

  const contextValue = useMemo(
    () => ({
      inputs,
      updateInputs,
      otpReferenceId,
      nextStep,
      stepTo,
      serviceInputs,
      updateServiceInputs,
      servicePackages,
      imageFiles,
      uploadedUrls,
      registrationState,
    }),
    [imageFiles, uploadedUrls, inputs, nextStep, stepTo, otpReferenceId, serviceInputs, servicePackages, registrationState]
  );

  const FORM_STEPS = 6; // steps 1-6 collect data; step 7 is the thank-you screen
  const stepLabel = STEP_LABELS[currentStep - 1];

  return (
    <>
      <Header isRegisterProfessionalPage />

      {currentStep <= FORM_STEPS && (
        <Box maxWidth={540} mx='auto' px={2} mt={{ xs: 3, md: 5 }}>
          <Stack direction='row' justifyContent='space-between' alignItems='baseline' mb={1}>
            <Typography variant='overline' color={COLORS.teal}>
              Step {currentStep} of {FORM_STEPS}
            </Typography>
            {stepLabel && (
              <Typography variant='overline' color={COLORS.inkSoft}>
                {stepLabel}
              </Typography>
            )}
          </Stack>
          <Box sx={{ height: 6, borderRadius: 999, bgcolor: COLORS.surfaceAlt, overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: `${(currentStep / FORM_STEPS) * 100}%`,
                borderRadius: 999,
                background: `linear-gradient(90deg, ${COLORS.teal}, ${COLORS.tealDark})`,
                transition: 'width .45s cubic-bezier(0.22,1,0.36,1)',
              }}
            />
          </Box>
        </Box>
      )}

      <FormContext.Provider value={contextValue}>
        <CurrentStep />
      </FormContext.Provider>
    </>
  );
}

export default Registration;
